import React from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  InputAdornment,
  Pagination,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  CardStoreList,
  CardStoreRow,
  CardStyle,
  ChipPresets,
  listCardStores,
} from "../../../../service/BuyerConfiguration/BuyerAppDashboard/restaurantCardsService";
import { GREEN, GREEN_DARK, errMsg } from "../ThemeStudio/themeUtils";
import { RestaurantCardEditor } from "./RestaurantCardEditor";

const PAGE_SIZE = 20;

export const CardRestaurantsTab: React.FC<{
  globalStyle: CardStyle;
  chipPresets: ChipPresets;
  onToast: (msg: string, ok: boolean) => void;
}> = ({ globalStyle, chipPresets, onToast }) => {
  const [search, setSearch] = React.useState("");
  const [query, setQuery] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [data, setData] = React.useState<CardStoreList | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [editing, setEditing] = React.useState<CardStoreRow | null>(null);
  const reqId = React.useRef(0);

  // Debounce the search box.
  React.useEffect(() => {
    const t = window.setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(t);
  }, [search]);

  const load = React.useCallback(async () => {
    const id = ++reqId.current;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await listCardStores({ search: query, page, limit: PAGE_SIZE });
      if (id === reqId.current) setData(res);
    } catch (e) {
      if (id === reqId.current) setLoadError(errMsg(e, "Could not load restaurants. Check your connection and try again."));
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, [query, page]);

  React.useEffect(() => {
    load();
  }, [load]);

  const pages = data ? Math.max(1, Math.ceil(data.total / (data.limit || PAGE_SIZE))) : 1;

  return (
    <Box sx={{ maxWidth: 900 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search restaurants"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, bgcolor: "#fff" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {data && (
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {data.total} restaurant{data.total === 1 ? "" : "s"}
          </Typography>
        )}
      </Stack>

      {loadError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={load} sx={{ textTransform: "none" }}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      ) : !data && loading ? (
        <Box sx={{ py: 8, textAlign: "center" }}>
          <CircularProgress sx={{ color: GREEN }} />
        </Box>
      ) : data && data.stores.length === 0 ? (
        <Card sx={{ borderRadius: 3, p: 4, textAlign: "center", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
          <Typography sx={{ fontWeight: 600 }}>{query ? `No restaurants match “${query}”.` : "No restaurants yet."}</Typography>
        </Card>
      ) : (
        <Card sx={{ borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.06)", opacity: loading ? 0.6 : 1, transition: "opacity 150ms" }}>
          {data?.stores.map((s, i) => (
            <Stack
              key={s.id}
              direction="row"
              alignItems="center"
              spacing={1.5}
              role="button"
              tabIndex={0}
              onClick={() => setEditing(s)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setEditing(s)}
              sx={{
                px: 2,
                py: 1.25,
                cursor: "pointer",
                borderTop: i ? "1px solid rgba(0,0,0,0.06)" : "none",
                "&:hover": { bgcolor: "rgba(0,177,79,0.04)" },
              }}
            >
              <Avatar src={s.logo_url ?? undefined} alt="" sx={{ width: 40, height: 40, bgcolor: "#E8F7EF", color: GREEN_DARK }}>
                {s.name?.charAt(0).toUpperCase()}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14.5 }} noWrap>
                  {s.name}
                </Typography>
                <Stack direction="row" spacing={0.75} sx={{ mt: 0.25 }} flexWrap="wrap" useFlexGap>
                  {s.customised && (
                    <Chip
                      size="small"
                      label={s.enabled ? "Customised" : "Customised · off"}
                      sx={{
                        height: 20,
                        fontSize: 11,
                        fontWeight: 600,
                        bgcolor: s.enabled ? "rgba(0,177,79,0.12)" : "rgba(0,0,0,0.06)",
                        color: s.enabled ? GREEN_DARK : "text.secondary",
                      }}
                    />
                  )}
                  {s.pin_rank !== null && s.pin_rank !== undefined && (
                    <Chip
                      size="small"
                      icon={<PushPinOutlinedIcon sx={{ fontSize: "13px !important" }} />}
                      label={`Pinned #${s.pin_rank}`}
                      sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: "#FFF3E0", color: "#B54708" }}
                    />
                  )}
                  {s.status && s.status.toLowerCase() !== "active" && (
                    <Chip size="small" label={s.status} sx={{ height: 20, fontSize: 11, textTransform: "capitalize" }} />
                  )}
                </Stack>
              </Box>
              {s.cover_image_url && (
                <Box
                  component="img"
                  src={s.cover_image_url}
                  alt=""
                  sx={{ width: 64, height: 40, objectFit: "cover", borderRadius: 1.5, display: { xs: "none", sm: "block" } }}
                />
              )}
              <ChevronRightIcon sx={{ color: "text.secondary" }} />
            </Stack>
          ))}
        </Card>
      )}

      {pages > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination
            count={pages}
            page={page}
            onChange={(_, p) => setPage(p)}
            sx={{ "& .Mui-selected": { bgcolor: `${GREEN} !important`, color: "#fff" } }}
          />
        </Stack>
      )}

      {editing && (
        <RestaurantCardEditor
          storeId={editing.id}
          storeName={editing.name}
          globalStyle={globalStyle}
          chipPresets={chipPresets}
          onClose={() => setEditing(null)}
          onChanged={load}
          onToast={onToast}
        />
      )}
    </Box>
  );
};

export default CardRestaurantsTab;
